import type { RefObject } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { apiFetch } from './api';

interface Options {
  articleId: string;
  /** Server-known progress; null/undefined while the article is loading. */
  savedProgress: number | null | undefined;
  /** Skip restore when the article is already 'read' — those should land at the top. */
  status: string;
  /** Restore needs scrollHeight to reflect the full article, so wait for content. */
  contentReady: boolean;
  /**
   * Scroller to read/write. Provide a ref for an inner-div scroller (split-pane
   * reader), or leave undefined to use the document/window (full-page reader).
   * The ref's identity must be stable across renders.
   */
  scrollRef?: RefObject<HTMLElement | null>;
  /** Sparse-write tuning for the PATCH that follows scroll. */
  debounceMs?: number;
}

/**
 * Restores scroll once content is in the DOM, then PATCHes readingProgress
 * (debounced) as the user reads.
 *
 * `markDeleted()` must be called before unmounting after a DELETE — without
 * it the trailing debounced PATCH races the DELETE and resurrects the row.
 */
export function useScrollProgress(opts: Options) {
  const { articleId, savedProgress, status, contentReady, scrollRef, debounceMs = 1500 } = opts;
  const [liveProgress, setLiveProgress] = useState<number>(savedProgress ?? 0);
  const progressRef = useRef<number>(savedProgress ?? 0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const restoredRef = useRef(false);
  const deletedRef = useRef(false);

  // Stash the scroller ref in a ref of its own so callbacks/effects stay stable
  // even if the caller passes a fresh options object each render.
  const scrollRefHolder = useRef(scrollRef);
  useEffect(() => {
    scrollRefHolder.current = scrollRef;
  }, [scrollRef]);

  const measure = useCallback((): { el: HTMLElement | null; top: number; total: number } => {
    const el = scrollRefHolder.current?.current ?? null;
    if (el) return { el, top: el.scrollTop, total: el.scrollHeight - el.clientHeight };
    const doc = document.documentElement;
    return { el: null, top: doc.scrollTop, total: doc.scrollHeight - doc.clientHeight };
  }, []);

  // Sync the live progress display when the server-known value arrives later.
  useEffect(() => {
    const saved = savedProgress ?? 0;
    progressRef.current = saved;
    setLiveProgress(saved);
  }, [savedProgress]);

  // Restore scroll exactly once: only after content is in the DOM, only when
  // we have somewhere to go, never for finished articles. If layout hasn't
  // settled yet (total === 0), leave restoredRef false so a later render retries.
  useEffect(() => {
    if (restoredRef.current) return;
    if (!contentReady) return;
    const saved = savedProgress ?? 0;
    if (saved <= 0 || status === 'archived' || status === 'filed') {
      restoredRef.current = true;
      return;
    }
    const m = measure();
    if (m.total <= 0) return;
    setScroll(m.el, (m.total * saved) / 100);
    restoredRef.current = true;
  }, [contentReady, savedProgress, status, measure]);

  const onScroll = useCallback(() => {
    if (deletedRef.current) return;
    const m = measure();
    if (m.total <= 0) return;
    const pct = Math.min(100, Math.round((m.top / m.total) * 100));
    if (pct === progressRef.current) return;
    progressRef.current = pct;
    setLiveProgress(pct);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      if (deletedRef.current) return;
      apiFetch(`/articles/${articleId}`, {
        method: 'PATCH',
        body: JSON.stringify({ readingProgress: pct }),
      }).catch(() => {});
    }, debounceMs);
  }, [articleId, measure, debounceMs]);

  const markDeleted = useCallback(() => {
    deletedRef.current = true;
    clearTimeout(timerRef.current);
  }, []);

  return { liveProgress, onScroll, markDeleted };
}

function setScroll(el: HTMLElement | null, top: number): void {
  if (el) el.scrollTop = top;
  else window.scrollTo(0, top);
}
